// pages/HomePage.js — Inicio = CENTRO DE CONTROL en PANELES (tanda inicio-fase-c C-B2,
// 5-oct-2026). Diez paneles en el orden aprobado por Miguel (doc §8): cada panel trae título,
// cifra grande, ALCANCE de tiempo visible y el detalle en lista (hasta 8 filas) con «ver todo»
// al módulo filtrado. Dos columnas independientes que se llenan de arriba abajo (sin huecos);
// en celular una columna y cada panel COLAPSABLE (título + cifra siempre visibles).
// Cada panel su fetch, y carga/falla por separado: cargando / sin datos / error / con datos son
// estados DISTINTOS y un error jamás se pinta como 0. CADA cifra reusa el servicio dueño
// (endpoints /api/inicio/* de C-B1), nunca se recalcula acá. Permisos: el backend filtra por
// rol; la UI además no PIDE lo que el rol no puede ver (cero fetches que morirían en 403).
// Textos genéricos multi-tienda, tuteo. Contrato: docs/inicio-centro-control-2026-10-04.md §§8-12.
import React, { useEffect, useState } from 'react';
import { semaforoDelJob } from '../utils/jobHealth';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('es-CO'));
const fmtCOP = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString('es-CO')}`);

// Fechas 'YYYY-MM-DD' de la TIENDA: se formatean por aritmética UTC sobre el string, nunca con
// la zona del navegador (espejo del criterio del backend).
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const DIAS_ABREV = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_ABREV = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const parseYmd = (ymd) => String(ymd || '').split('-').map(Number);
const diaSemana = (ymd) => { const [y, m, d] = parseYmd(ymd); return DIAS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] || ''; };
const diaAbrev = (ymd) => { const [y, m, d] = parseYmd(ymd); return DIAS_ABREV[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] || ''; };
// «lunes» ya es plural; sábado/domingo ganan la s.
const diaSemanaPlural = (ymd) => { const n = diaSemana(ymd); return (n === 'sábado' || n === 'domingo') ? `${n}s` : n; };
const fechaCorta = (ymd) => { const [, m, d] = parseYmd(ymd); return m ? `${diaAbrev(ymd)} ${d} ${MESES_ABREV[m - 1]}` : '—'; };
const edadTexto = (ymd, hoy) => {
  const [y1, m1, d1] = parseYmd(ymd); const [y2, m2, d2] = parseYmd(hoy);
  if (!y1 || !y2) return '';
  const dias = Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  return `hace ${dias} días`;
};

// C-B4 → recompra-B4: mayúscula inicial SOLO al mostrar — el dato no se toca (prod trae
// nombres y ciudades en cualquier caja: «Santa MArta», «BOGOTÁ»). Primera letra de cada
// palabra en mayúscula y el RESTO en minúscula («Santa MArta» → «Santa Marta»). La comparten
// el Centro de control y la pestaña Recompra.
export function mayusculaInicial(s) {
  return String(s || '').toLocaleLowerCase('es-CO').replace(/(^|\s)(\S)/g, (m, sep, ch) => sep + ch.toLocaleUpperCase('es-CO'));
}

// C-B4: reparto de paneles en DOS columnas por ALTURA REAL — cada panel cae a la columna más
// corta hasta ese momento, conservando el orden aprobado como prioridad (greedy en orden, nunca
// reordena). Sin medición todavía (o panel oculto) se usa la estimada, así el primer render ya
// reparte. Pura y exportada para test.
export function repartirEnColumnas(paneles, alturas = {}, estimada = 320) {
  const cols = [[], []];
  const alto = [0, 0];
  for (const p of (paneles || [])) {
    const c = alto[0] <= alto[1] ? 0 : 1;
    cols[c].push(p);
    alto[c] += alturas[p.id] != null ? Number(alturas[p.id]) || 0 : estimada;
  }
  return cols;
}

// Canal → badge corto. Los fallbacks largos de canalDe («Creado en wp-admin (sin canal)», …)
// se muestran acortados con el texto completo en title — no se inventa un canal.
const BADGE_CANAL = { Web: 'web', 'Tienda física': 'fisica', Feria: 'feria', WhatsApp: 'wpp' };
function BadgeCanal({ canal }) {
  if (!canal) return null;
  const tono = BADGE_CANAL[canal];
  if (tono) return <span className={`pnl-badge pnl-badge--${tono}`}>{canal === 'Tienda física' ? 'Tienda' : canal}</span>;
  return <span className="pnl-chip" title={canal}>{/sin canal/i.test(canal) ? 'sin canal' : canal}</span>;
}

// ── Fila de pedido (anatomía §8, afinada en C-B3): 1.ª línea SOLO nombre + ciudad + VALOR a la
// derecha (el valor no baja nunca: la línea no envuelve y nombre/ciudad se recortan con …);
// 2.ª línea las ETIQUETAS (pago, canal, edad, transportadora, quién despachó); debajo TODOS los
// productos, uno por línea con cantidad. Un presencial sin ciudad: se omite, no se inventa.
function FilaPedido({ f, etiquetas = null }) {
  return (
    <div className="pnl-ped">
      <div className="pnl-ped-top" data-testid="ped-top">
        <span className="pnl-ped-num">#{f.numero}</span>
        <span className="pnl-ped-quien">{mayusculaInicial(f.cliente)}</span>
        {f.ciudad ? <span className="pnl-ped-ciudad">{mayusculaInicial(f.ciudad)}</span> : null}
        <span className="pnl-sp" />
        <span className="pnl-ped-val">{fmtCOP(f.valor)}</span>
      </div>
      {etiquetas ? <div className="pnl-ped-tags" data-testid="ped-tags">{etiquetas}</div> : null}
      <div className="pnl-ped-prods">
        {(f.productos || []).map((p, i) => <div key={i}>{p.cantidad}× {p.nombre}</div>)}
      </div>
    </div>
  );
}

const MAX_FILAS = 8; // con filas altas, hasta 8 pedidos por panel; el resto vive tras «ver todo»

// ── Piezas visuales ──────────────────────────────────────────────────────────────────────────
// Mini-barras de los últimos 7 días (D2; corrección c: SIEMPRE visibles — alto fijo y piso de 6%).
function MiniSerie({ serie }) {
  const max = Math.max(1, ...serie.map((f) => Number(f.ingresos) || 0));
  return (
    <div className="pnl-mini" data-testid="mini-serie">
      {serie.map((f) => (
        <div key={f.fecha} className="pnl-mini-col" title={`${f.fecha}: ${fmtCOP(f.ingresos)}`}>
          <div data-testid="mini-barra" className={`pnl-mini-b${f.parcial ? ' es-hoy' : ''}`} style={{ height: `${Math.max(6, Math.round(((Number(f.ingresos) || 0) / max) * 100))}%` }} />
          <span className="pnl-mini-d">{f.parcial ? 'hoy' : diaAbrev(f.fecha)}</span>
        </div>
      ))}
    </div>
  );
}

// Hoy (parcial) contra el promedio del mismo día (8 semanas completas) — mismas unidades,
// misma serie; la nota declara que el día va corriendo (nunca «vas abajo» sobre un parcial).
function ComparacionDia({ c, fecha }) {
  const max = Math.max(1, Number(c.hoy_ingresos) || 0, Number(c.promedio) || 0);
  const ancho = (v) => `${Math.max(2, Math.round(((Number(v) || 0) / max) * 100))}%`;
  return (
    <div className="pnl-comp">
      <div className="pnl-comp-fila">
        <span className="pnl-comp-etq">Hoy (parcial)</span>
        <div className="pnl-comp-track"><div className="pnl-comp-b es-hoy" style={{ width: ancho(c.hoy_ingresos) }} /></div>
        <span className="pnl-comp-val">{fmtCOP(c.hoy_ingresos)}</span>
      </div>
      <div className="pnl-comp-fila">
        <span className="pnl-comp-etq">{diaSemana(fecha) ? `${diaSemana(fecha).charAt(0).toUpperCase()}${diaSemana(fecha).slice(1)} típico` : 'Día típico'}</span>
        <div className="pnl-comp-track"><div className="pnl-comp-b" style={{ width: ancho(c.promedio) }} /></div>
        <span className="pnl-comp-val">{fmtCOP(c.promedio)}</span>
      </div>
      <p className="pnl-nota">
        Promedio de los últimos {c.dias_medidos} {diaSemanaPlural(fecha)} (días completos). El día de hoy aún no termina: las dos cifras no son comparables todavía.
      </p>
    </div>
  );
}

// ── Los PANELES: definición declarativa, exportada para test ─────────────────────────────────
// { id, admin, soloSiHay?, path, destino, hash?, verTodo, titulo,
//   alcance(d), chips?(d), cifra(d) → {grande, sub?}, sinDatos?(d) → frase|null,
//   alDia?(d) → frase|null (panel compacto en verde), cuerpo(d, ctx) → JSX }
export const PANELES = [
  {
    id: 'vendido-hoy', admin: false,
    path: () => '/api/inicio/vendido-hoy', destino: ['orders'], verTodo: 'Ver todo en Pedidos →',
    titulo: 'Vendido hoy',
    alcance: () => 'hoy · va corriendo',
    cifra: (d) => ({
      grande: fmtCOP(d.ventas && d.ventas.ingresos_netos),
      sub: `· ${fmtInt(d.ventas && d.ventas.pedidos)} ${Number(d.ventas && d.ventas.pedidos) === 1 ? 'pedido' : 'pedidos'}`,
    }),
    sinDatos: (d) => (d.ventas ? null : 'Las ventas de hoy no están disponibles.'),
    cuerpo: (d) => (
      <>
        {d.comparacion ? <ComparacionDia c={d.comparacion} fecha={d.fecha} /> : null}
        {Array.isArray(d.serie_7_dias) ? <MiniSerie serie={d.serie_7_dias} /> : null}
        <div className="pnl-sep">Pedidos de hoy</div>
        {(d.filas || []).length === 0
          ? <p className="pnl-vacio">Todavía no hay ventas hoy.</p>
          : (d.filas || []).slice(0, MAX_FILAS).map((f) => (
            <FilaPedido key={f.numero} f={f} etiquetas={f.canal ? <BadgeCanal canal={f.canal} /> : null} />
          ))}
      </>
    ),
  },
  {
    id: 'por-despachar', admin: false,
    path: () => '/api/inicio/por-despachar', destino: ['orders'], verTodo: 'Ver todo en Pedidos →',
    titulo: 'Por despachar',
    alcance: () => 'ahora',
    cifra: (d) => ({ grande: fmtInt(d.total), sub: Number(d.total) === 1 ? 'pedido' : 'pedidos' }),
    alDia: (d) => (Number(d.total) === 0 ? 'Nada pendiente de despacho' : null),
    cuerpo: (d) => (
      <>
        {(d.filas || []).slice(0, MAX_FILAS).map((f) => (
          <FilaPedido key={f.numero} f={f} etiquetas={f.transportadora ? <span className="pnl-chip">{f.transportadora}</span> : null} />
        ))}
        <p className="pnl-nota">La transportadora aparece solo cuando ya se asignó (se captura al despachar).</p>
      </>
    ),
  },
  {
    id: 'por-confirmar', admin: false,
    path: () => '/api/inicio/por-confirmar', destino: ['orders'], verTodo: 'Ver todo en Pedidos →',
    titulo: 'Pagos por confirmar',
    alcance: () => 'ahora',
    cifra: (d) => ({ grande: fmtInt(d.pedidos), sub: `${Number(d.pedidos) === 1 ? 'pedido' : 'pedidos'} · ${fmtCOP(d.valor)}` }),
    alDia: (d) => (Number(d.pedidos) === 0 ? 'Ningún pago por confirmar' : null),
    cuerpo: (d, { hoy }) => (
      <>
        {(d.esta_semana || []).length > 0 && <div className="pnl-sep">Esta semana</div>}
        {(d.esta_semana || []).slice(0, MAX_FILAS).map((f) => (
          <FilaPedido
            key={f.numero} f={f}
            etiquetas={<>{f.pago ? <span className="pnl-chip">{f.pago}</span> : null}<BadgeCanal canal={f.canal} /><span className="pnl-ped-meta">{edadTexto(f.fecha, hoy)}</span></>}
          />
        ))}
        {(d.viejos || []).length > 0 && (
          <div className="pnl-sep">Más de 7 días <span className="pnl-chip pnl-chip--warn">quizá toca cancelar</span></div>
        )}
        {(d.viejos || []).slice(0, MAX_FILAS).map((f) => (
          <FilaPedido
            key={f.numero} f={f}
            etiquetas={<>{f.pago ? <span className="pnl-chip">{f.pago}</span> : null}<BadgeCanal canal={f.canal} /><span className="pnl-ped-meta">{edadTexto(f.fecha, hoy)}</span></>}
          />
        ))}
      </>
    ),
  },
  {
    // B2 · D7 (7-oct-2026): «Clientes del mes» — las MISMAS cifras de la pestaña Recompra
    // de Clientes (una definición: construirPerfiles), al grano del mes calendario en curso.
    id: 'clientes-del-mes', admin: true,
    path: () => '/api/inicio/clientes-del-mes', destino: ['customers'], verTodo: 'Ver más en Clientes → Recompra →',
    titulo: 'Clientes del mes',
    alcance: (d) => {
      const [, m] = parseYmd(`${d.mes || ''}-01`);
      return m ? MESES[m - 1] : 'este mes';
    },
    cifra: (d) => ({ grande: fmtInt(d.clientes_compraron), sub: Number(d.clientes_compraron) === 1 ? 'clienta compró' : 'clientas compraron' }),
    sinDatos: (d) => (Number(d.clientes_compraron) === 0 ? 'Todavía no hay compras identificadas este mes.' : null),
    cuerpo: (d) => (
      <>
        <div className="pnl-fila"><span className="pnl-fila-main">Nuevas (primera compra)</span><span className="pnl-fila-val">{fmtInt(d.nuevos)}</span></div>
        <div className="pnl-fila"><span className="pnl-fila-main">Recurrentes</span><span className="pnl-fila-val">{fmtInt(d.recurrentes)}</span></div>
        <div className="pnl-fila"><span className="pnl-fila-main">Dormidas (sin comprar en {d.umbral_dormidas || 60} días)</span><span className="pnl-fila-val">{fmtInt(d.dormidas)}</span></div>
        <p className="pnl-nota">Una clienta = su cuenta o su correo; el teléfono solo desempata cuando no es compartido.</p>
      </>
    ),
  },
  {
    id: 'como-va-el-mes', admin: true,
    path: () => '/api/inicio/como-va-el-mes', destino: ['finanzas'], verTodo: 'Ver detalle en Finanzas →',
    titulo: 'Cómo va el mes',
    alcance: (d) => {
      const [, m] = parseYmd(`${d.periodo || ''}-01`);
      return m ? `${MESES[m - 1]} · día ${d.dias_transcurridos} de ${d.dias_del_mes}` : 'este mes';
    },
    chips: (d) => {
      // Con el gate cerrado la cobertura viaja en el TOP del payload (no bajo meta).
      const pct = (d.meta && d.meta.cobertura_de_costo_pct) != null ? d.meta.cobertura_de_costo_pct : d.cobertura_de_costo_pct;
      return pct != null ? [`margen estimado · cobertura ${pct}%`] : [];
    },
    // C-B3: la cifra grande son las VENTAS del mes y NO depende del equilibrio — el gate del
    // margen cierra la meta, no lo vendido (que está medido). ventas_del_mes viaja siempre.
    cifra: (d) => ({ grande: fmtCOP(d.ventas_del_mes), sub: 'vendidos' }),
    cuerpo: (d) => {
      const meta = d.meta || {};
      const cv = d.como_vas || {};
      // C-B3: lo que falta se declara en UNA línea corta; el «Ver detalle en Finanzas →» del
      // panel es el enlace. Nada de motivo largo tapando el panel.
      const sinEquilibrio = d.base_disponible === false
        ? 'No se puede calcular el punto de equilibrio: faltan costos cargados.'
        : (meta.sin_gastos_fijos ? 'No se puede calcular el punto de equilibrio: no hay gastos fijos cargados este mes.' : null);
      const necesaria = sinEquilibrio ? null : meta.venta_necesaria_para_no_perder;
      const pctVendido = (cv.medible && necesaria > 0) ? Math.min(100, Math.round((cv.vendido_en_el_mes / necesaria) * 100)) : 0;
      const pctEsperado = (cv.esperado_a_esta_altura_del_mes != null && necesaria > 0)
        ? Math.min(100, Math.round((cv.esperado_a_esta_altura_del_mes / necesaria) * 100)) : null;
      const comp = d.comparacion_mes_anterior;
      return (
        <>
          {sinEquilibrio ? <p className="pnl-vacio">{sinEquilibrio}</p> : null}
          {necesaria != null && (
            <div className="pnl-barra-wrap">
              <div className="pnl-barra">
                <div className="pnl-barra-fill" style={{ width: `${pctVendido}%` }} />
                {pctEsperado != null && <div className="pnl-barra-marca" style={{ left: `${pctEsperado}%` }} title="Esperado a esta altura" />}
              </div>
              <div className="pnl-barra-leyenda">
                <span>▮ esperado a esta altura: {fmtCOP(cv.esperado_a_esta_altura_del_mes)}</span>
                <span>para no perder: <b>{fmtCOP(necesaria)}</b></span>
              </div>
            </div>
          )}
          {!sinEquilibrio && !cv.medible && cv.nota ? <p className="pnl-vacio">{cv.nota}</p> : null}
          {!sinEquilibrio && cv.medible && cv.ritmo ? (
            <div className="pnl-fila">
              <span className="pnl-fila-main">Con ritmo parejo, a esta altura lo esperado era {fmtCOP(cv.esperado_a_esta_altura_del_mes)}.</span>
              <span className={`pnl-chip ${/adelantado/.test(cv.ritmo) ? 'pnl-chip--ok' : 'pnl-chip--warn'}`}>
                {/adelantado/.test(cv.ritmo) ? 'vas adelantado' : 'vas atrasado'}
              </span>
            </div>
          ) : null}
          {comp && comp.vendido_anterior != null ? (
            <div className="pnl-fila">
              <span className="pnl-fila-main">
                Mismo tramo del mes pasado ({fechaCorta(comp.ventana_anterior && comp.ventana_anterior.desde)} – {fechaCorta(comp.ventana_anterior && comp.ventana_anterior.hasta)}): <b>{fmtCOP(comp.vendido_anterior)}</b>
              </span>
              {comp.variacion_pct != null && (
                <span className={`pnl-chip ${comp.variacion_pct >= 0 ? 'pnl-chip--ok' : 'pnl-chip--warn'}`}>
                  {comp.variacion_pct >= 0 ? '+' : ''}{comp.variacion_pct}%
                </span>
              )}
            </div>
          ) : null}
          {!sinEquilibrio && <p className="pnl-nota">La meta usa los gastos fijos cargados del mes. Si falta alguno (un pago puntual, un arreglo), la meta sale más baja de lo real — revisa la lista en Finanzas.</p>}
        </>
      );
    },
  },
  {
    id: 'pagos-semana', admin: true,
    path: () => '/api/inicio/pagos-semana', destino: ['finanzas'], verTodo: 'Ver todo en Finanzas →',
    titulo: 'Pagos de esta semana',
    alcance: () => 'próximos 7 días',
    cifra: (d) => {
      const filas = d.filas || [];
      return {
        grande: fmtCOP(filas.reduce((s, f) => s + (Number(f.monto) || 0), 0)),
        sub: `· ${filas.length} ${filas.length === 1 ? 'pago' : 'pagos'}`,
      };
    },
    cuerpo: (d) => (
      <>
        {(d.filas || []).length === 0
          ? <p className="pnl-aldia-linea">✓ Ninguna fecha de pago cae esta semana.</p>
          : (d.filas || []).map((f) => (
            <div className="pnl-fila" key={`${f.concepto}-${f.fecha}`}>
              <span className="pnl-fila-main">{f.concepto} <span className="pnl-sec">· {fechaCorta(f.fecha)}</span></span>
              {f.es_estimado ? <span className="pnl-chip">estimado</span> : null}
              <span className="pnl-fila-val">{fmtCOP(f.monto)}</span>
            </div>
          ))}
        {d.quedan_del_mes
          ? (
            <p className="pnl-pie">
              {d.quedan_del_mes.pagos === 1
                ? <>Este mes queda 1 pago por {fmtCOP(d.quedan_del_mes.monto)}.</>
                : <>Este mes quedan {d.quedan_del_mes.pagos} pagos por {fmtCOP(d.quedan_del_mes.monto)}.</>}
            </p>
          )
          : <p className="pnl-pie">Sin gastos recurrentes configurados este mes.</p>}
      </>
    ),
  },
  {
    id: 'cajas', admin: true,
    path: () => '/api/inicio/cajas', destino: ['cashflow'], verTodo: 'Ver movimientos en Caja →',
    titulo: 'Cajas',
    alcance: () => 'ahora',
    cifra: (d) => ({ grande: fmtCOP(d.total), sub: 'en total' }),
    sinDatos: (d) => (Array.isArray(d.cajas) ? null : 'Los saldos de las cajas no están disponibles.'),
    cuerpo: (d) => (
      <>
        {(d.cajas || []).map((c) => (
          <div className="pnl-fila" key={c.nombre}>
            <span className="pnl-fila-main">{c.nombre}</span>
            <span className="pnl-fila-val">{fmtCOP(c.saldo)}</span>
          </div>
        ))}
      </>
    ),
  },
  {
    id: 'gestionados-hoy', admin: false,
    path: () => '/api/inicio/gestionados-hoy', destino: ['orders'], verTodo: 'Ver todo en Pedidos →',
    titulo: 'Gestionados hoy',
    alcance: () => 'hoy',
    cifra: (d) => ({ grande: fmtInt(d.pedidos), sub: Number(d.pedidos) === 1 ? 'pedido' : 'pedidos' }),
    cuerpo: (d) => (
      <>
        {(d.filas || []).length === 0
          ? <p className="pnl-vacio">Todavía no se ha despachado ni completado ningún pedido hoy.</p>
          : (d.filas || []).slice(0, MAX_FILAS).map((f) => (
            <FilaPedido key={f.numero} f={f} etiquetas={<span className="pnl-ped-meta">{f.quien ? `despachó ${f.quien}` : 'completado · sin responsable'}</span>} />
          ))}
        <p className="pnl-nota">Un pedido completado directo en la tienda no pasó por el flujo de despacho: va sin responsable.</p>
      </>
    ),
  },
  {
    id: 'se-agota', admin: false,
    // C-B3: el footer genérico se apaga (verTodo null) — los enlaces viven SEPARADOS dentro del
    // cuerpo: «Ver todo en Inventario →» tras la tabla y «Ver Compras →» en la sección de llegadas.
    path: () => '/api/inicio/se-agota-y-se-mueve', destino: ['inventory'], verTodo: null,
    titulo: 'Se agota y se mueve',
    alcance: () => 'stock ahora · movimiento últimos 7 días',
    chips: (d) => {
      const s = d.sensor || {};
      if (!s.configurado) return ['sensor sin configurar'];
      return [`sensor: ${s.dias_con_corte} de ${s.dias} días con datos`];
    },
    // C-B3: la cifra grande son las que SE MUEVEN (el backend ya filtra y ordena por
    // vendidas → vistas → carrito); las quietas van contadas en el pie.
    cifra: (d) => ({
      grande: fmtInt((d.filas || []).length),
      sub: (d.filas || []).length === 1 ? 'variación que se mueve y se agota' : 'variaciones que se mueven y se agotan',
    }),
    cuerpo: (d, { esAdmin, onIr }) => (
      <>
        {(d.filas || []).length === 0
          ? <p className="pnl-aldia-linea">✓ Nada de lo que se mueve está por agotarse.</p>
          : (
            <table className="pnl-tabla">
              <thead>
                <tr><th>Producto</th><th className="num">Stock</th><th className="num">Vendidas 7d</th><th className="num">Vistas 7d*</th><th className="num">Carrito 7d*</th></tr>
              </thead>
              <tbody>
                {(d.filas || []).slice(0, MAX_FILAS).map((f) => (
                  <tr key={`${f.productId}-${f.variationId}`}>
                    <td>{f.producto}{f.variacion ? ` – ${f.variacion}` : ''}</td>
                    <td className="num crit">{fmtInt(f.stock)}</td>
                    <td className="num">{fmtInt(f.vendidas)}</td>
                    <td className="num">{f.sensor ? fmtInt(f.sensor.vistas) : '—'}</td>
                    <td className="num">{f.sensor ? fmtInt(f.sensor.carrito) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {(d.filas || []).length > 0 && (
          <p className="pnl-nota">* Vistas y carrito son del producto completo: el sensor no separa por talla ni tono.</p>
        )}
        {Number(d.sin_movimiento) > 0 && (
          <button type="button" className="pnl-vertodo pnl-vertodo--sub" onClick={() => onIr(['inventory'])}>
            {fmtInt(d.sin_movimiento)} más por agotarse sin movimiento esta semana →
          </button>
        )}
        <button type="button" className="pnl-vertodo" onClick={() => onIr(['inventory'])}>Ver todo en Inventario →</button>
        {esAdmin && <SeccionLlegadas onIr={onIr} />}
      </>
    ),
  },
  {
    id: 'tienda-web', admin: true,
    path: () => '/api/inicio/tienda-web', destino: ['analitica'], hash: '#analitica/web', verTodo: 'Ver todo en Analítica →',
    titulo: 'Tienda web',
    alcance: () => 'última hora',
    cifra: (d) => ({ grande: fmtInt(d.sesiones_ultima_hora), sub: `${Number(d.sesiones_ultima_hora) === 1 ? 'sesión' : 'sesiones'} · ${fmtInt(d.sesiones_hoy)} hoy` }),
    sinDatos: (d) => (d.configurado === false ? 'El sensor de la tienda web no está configurado.' : null),
    cuerpo: (d) => (
      <>
        {d.sin_actividad ? <p className="pnl-vacio">Hoy no se ha medido actividad en tu tienda web (0 medido).</p> : null}
        {(d.viendo_ahora || []).length > 0 && (
          <>
            <div className="pnl-sep">Viendo ahora <span className="pnl-alcance">última hora</span></div>
            {d.viendo_ahora.map((f) => (
              <div className="pnl-fila" key={`va-${f.producto}`}>
                <span className="pnl-fila-main">{f.producto}</span>
                <span className="pnl-fila-meta">{fmtInt(f.vistas)} vistas · {fmtInt(f.carrito)} al carrito</span>
              </div>
            ))}
          </>
        )}
        {(d.mas_visto_hoy || []).length > 0 && (
          <>
            <div className="pnl-sep">Lo más visto <span className="pnl-alcance">hoy</span></div>
            {d.mas_visto_hoy.map((f) => (
              <div className="pnl-fila" key={`mv-${f.producto}`}>
                <span className="pnl-fila-main">{f.producto}</span>
                <span className="pnl-fila-meta">{fmtInt(f.vistas)} vistas · {fmtInt(f.carrito)} al carrito</span>
              </div>
            ))}
          </>
        )}
        {(d.carrito_hoy || []).length > 0 && (
          <>
            <div className="pnl-sep">Dejado en el carrito <span className="pnl-alcance">hoy</span></div>
            {d.carrito_hoy.map((f) => (
              <div className="pnl-fila" key={`ch-${f.producto}-${f.variacion}`}>
                <span className="pnl-fila-main">{f.producto}{f.variacion && f.variacion !== '(sin variación)' ? ` – ${f.variacion}` : ''}</span>
                <span className="pnl-fila-val">×{fmtInt(f.veces)}</span>
              </div>
            ))}
          </>
        )}
        {d.carrito_7_dias && (d.carrito_7_dias.filas || []).length > 0 && (
          <>
            <div className="pnl-sep">
              Dejado en el carrito <span className="pnl-alcance">últimos 7 días</span>
              <span className="pnl-chip">{d.carrito_7_dias.dias_con_corte} de {d.carrito_7_dias.dias} medidos</span>
            </div>
            {d.carrito_7_dias.filas.map((f) => (
              <div className="pnl-fila" key={`c7-${f.producto}-${f.variacion}`}>
                <span className="pnl-fila-main">{f.producto}{f.variacion && f.variacion !== '(sin variación)' ? ` – ${f.variacion}` : ''}</span>
                <span className="pnl-fila-val">×{fmtInt(f.sesiones)}</span>
              </div>
            ))}
          </>
        )}
      </>
    ),
  },
  {
    // Solo-si-hay (decisión 3, intacta de B2): sin deriva el panel no existe; el enlace lleva a
    // la página de Logs existente prefiltrada por inventario.
    id: 'cambios-inventario', admin: true, soloSiHay: true,
    path: () => '/api/inicio/cambios-inventario', destino: ['users', 'audit', 'Inventario'], verTodo: 'Revisar en Auditoría →',
    titulo: 'Cambios de inventario',
    alcance: () => 'últimos 7 días',
    cifra: (d) => ({ grande: fmtInt(d.eventos), sub: Number(d.eventos) === 1 ? 'cambio de stock sin revisar' : 'cambios de stock sin revisar' }),
    aviso: true,
    cuerpo: () => <p className="pnl-nota">Cambios de stock hechos por fuera de Neto. Este panel solo aparece cuando hay algo sin revisar.</p>,
  },
];

// ── Hooks ────────────────────────────────────────────────────────────────────────────────────
function usePanelData(path) {
  const [st, setSt] = useState({ estado: 'cargando' });
  useEffect(() => {
    let off = false;
    fetch(`${API_URL}${path}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || `Error ${r.status}`);
        return data;
      })
      .then((data) => { if (!off) setSt({ estado: 'ok', data }); })
      .catch(() => { if (!off) setSt({ estado: 'error' }); });
    return () => { off = true; };
  }, [path]);
  return st;
}

// C-B4: alturas REALES de cada panel para el reparto en columnas. Un callback-ref por panel
// con ResizeObserver (colapsar/expandir o cargar datos re-reparte solo). En entornos sin
// ResizeObserver (tests) queda la medición inicial y el reparto cae a la estimada.
function useAlturasDePanel() {
  const [alturas, setAlturas] = useState({});
  const observers = React.useRef(new Map());
  const refsPorId = React.useRef(new Map());
  // El callback-ref se MEMOIZA por id: si cambiara en cada render, React lo re-engancharía
  // (null + nodo) y el observer se recrearía render a render.
  const medir = React.useCallback((id) => {
    if (!refsPorId.current.has(id)) {
      refsPorId.current.set(id, (el) => {
        const prev = observers.current.get(id);
        if (prev) { prev.disconnect(); observers.current.delete(id); }
        if (!el) return;
        const aplicar = () => {
          const h = el.offsetHeight || 0;
          setAlturas((a) => (Math.abs((a[id] != null ? a[id] : -1) - h) > 1 ? { ...a, [id]: h } : a));
        };
        aplicar();
        if (typeof ResizeObserver === 'function') {
          const ro = new ResizeObserver(aplicar);
          ro.observe(el);
          observers.current.set(id, ro);
        }
      });
    }
    return refsPorId.current.get(id);
  }, []);
  useEffect(() => {
    const vivos = observers.current;
    return () => { vivos.forEach((o) => o.disconnect()); vivos.clear(); };
  }, []);
  return { alturas, medir };
}

function useMediaAngosto() {
  const consulta = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 767px)').matches : false);
  const [angosto, setAngosto] = useState(consulta);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const f = () => setAngosto(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', f) : mq.addListener(f);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', f) : mq.removeListener(f); };
  }, []);
  return angosto;
}

// Hoy de la TIENDA (Bogotá, UTC-5 sin cambio de hora) — para «ayer / hace N días».
const hoyYmd = () => {
  const d = new Date(Date.now() - 5 * 3600 * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

// «Llegadas de proveedores» vive DENTRO de «Se agota y se mueve» pero es sección SOLO admin con
// su propio endpoint: se monta únicamente para admin (cero fetches muertos para otros roles).
export function SeccionLlegadas({ onIr }) {
  const st = usePanelData('/api/inicio/llegadas');
  return (
    <>
      <div className="pnl-sep">Llegadas de proveedores <span className="pnl-alcance">próximos 7 días</span></div>
      {st.estado === 'cargando' && <p className="pnl-vacio">…</p>}
      {st.estado === 'error' && <p className="pnl-vacio">No se pudo calcular. Intenta recargar.</p>}
      {st.estado === 'ok' && (() => {
        const d = st.data || {};
        const prox = d.proximas || [];
        const avisos = [];
        if ((d.atrasadas || []).length) avisos.push(`${d.atrasadas.length} ${d.atrasadas.length === 1 ? 'orden atrasada' : 'órdenes atrasadas'}`);
        if (d.sin_fecha) avisos.push(`${d.sin_fecha} ${d.sin_fecha === 1 ? 'orden abierta sin fecha estimada' : 'órdenes abiertas sin fecha estimada'}`);
        return (
          <>
            {prox.length === 0 && <p className="pnl-vacio">Ninguna llegada programada esta semana.</p>}
            {prox.slice(0, MAX_FILAS).map((o) => (
              <div className="pnl-fila" key={o.numero}>
                <span className="pnl-ped-num">{o.numero}</span>
                <span className="pnl-fila-main">{o.proveedor} <span className="pnl-sec">· {o.lineas} {o.lineas === 1 ? 'línea' : 'líneas'}</span></span>
                <span className="pnl-fila-meta">llega {fechaCorta(o.fecha_esperada)}</span>
              </div>
            ))}
            {avisos.length > 0 && <p className="pnl-nota">{avisos.join(' · ')}.</p>}
            <button type="button" className="pnl-vertodo pnl-vertodo--sub" onClick={() => onIr(['costs'])}>Ver Compras →</button>
          </>
        );
      })()}
    </>
  );
}

// ── El Panel: título + alcance + cifra (siempre visibles) y cuerpo colapsable ────────────────
export function Panel({ def, esAdmin, esCelular, primero, onNavigate }) {
  const st = usePanelData(def.path());
  const [abiertoManual, setAbiertoManual] = useState(null);
  const abierto = abiertoManual != null ? abiertoManual : (!esCelular || primero);
  const ir = () => {
    if (def.hash) window.location.hash = def.hash; // deep-link (AnalyticsPage lee el hash al montar)
    onNavigate(...def.destino);
  };
  const onIr = (destino) => onNavigate(...destino);

  if (st.estado === 'cargando') {
    if (def.soloSiHay) return null; // aparecería y desaparecería
    return (
      <section className="pnl pnl--cargando" data-testid={`panel-${def.id}`}>
        <header className="pnl-head"><div className="pnl-head-izq"><h3 className="pnl-titulo">{def.titulo}</h3><div className="pnl-cifra">…</div></div></header>
      </section>
    );
  }
  if (st.estado === 'error') {
    // Error ≠ 0: se dice que no se pudo, nunca se pinta un número que no se midió.
    return (
      <section className="pnl pnl--error" data-testid={`panel-${def.id}`}>
        <header className="pnl-head"><div className="pnl-head-izq"><h3 className="pnl-titulo">{def.titulo}</h3>
          <p className="pnl-vacio">No se pudo calcular. Intenta recargar.</p></div></header>
      </section>
    );
  }
  const d = st.data || {};
  const sinDatos = def.sinDatos ? def.sinDatos(d) : null;
  if (sinDatos) {
    return (
      <section className="pnl pnl--sindatos" data-testid={`panel-${def.id}`}>
        <header className="pnl-head"><div className="pnl-head-izq"><h3 className="pnl-titulo">{def.titulo}</h3>
          <p className="pnl-vacio">{sinDatos}</p></div></header>
      </section>
    );
  }
  if (def.soloSiHay) {
    const { grande } = def.cifra(d);
    if (!(Number(String(grande).replace(/[^\d]/g, '')) > 0)) return null;
  }
  const alDia = def.alDia ? def.alDia(d) : null;
  if (alDia) {
    // Cero pendientes = panel CHICO en verde: la ausencia se celebra, no se esconde.
    return (
      <section className="pnl pnl--aldia" data-testid={`panel-${def.id}`}>
        <button type="button" className="pnl-aldia-btn" onClick={ir}>
          <span className="pnl-check" aria-hidden="true">✓</span> {alDia}
        </button>
      </section>
    );
  }
  const { grande, sub } = def.cifra(d);
  const chips = def.chips ? def.chips(d) : [];
  return (
    <section className={`pnl${def.aviso ? ' pnl--aviso' : ''}`} data-testid={`panel-${def.id}`}>
      <header className="pnl-head">
        <button type="button" className="pnl-head-btn" onClick={() => setAbiertoManual(!abierto)} aria-expanded={abierto}>
          <div className="pnl-head-izq">
            <h3 className="pnl-titulo">
              {def.titulo} <span className="pnl-alcance">{def.alcance(d)}</span>
              {chips.map((c) => <span className="pnl-chip" key={c}>{c}</span>)}
            </h3>
            <div className="pnl-cifra">{grande} {sub ? <small>{sub}</small> : null}</div>
          </div>
          <span className={`pnl-caret${abierto ? ' abierto' : ''}`} aria-hidden="true">▶</span>
        </button>
      </header>
      {abierto && (
        <div className="pnl-cuerpo">
          {def.cuerpo(d, { esAdmin, hoy: hoyYmd(), onIr })}
          {def.verTodo ? <button type="button" className="pnl-vertodo" onClick={ir}>{def.verTodo}</button> : null}
        </div>
      )}
    </section>
  );
}

// Alerta CONDICIONAL del costeo automático (conservada de B2): solo si el semáforo da
// rojo/ámbar; para un vendedor el fetch falla y el catch silencioso la omite.
function useAlertaCosteo() {
  const [alerta, setAlerta] = useState(null);
  useEffect(() => {
    let cancelado = false;
    fetch(`${API_URL}/api/costing/reconcile-non-pos/health`)
      .then((r) => (r.ok ? r.json() : null))
      .then((h) => {
        if (cancelado || !h) return;
        const sem = semaforoDelJob(h);
        if (sem.tono === 'rojo' || sem.tono === 'ambar') setAlerta(`${sem.titulo}. ${sem.detalle} Ver Configuración → Estado del sistema.`);
      })
      .catch(() => {});
    return () => { cancelado = true; };
  }, []);
  return alerta;
}

// El día va en minúscula a propósito (Miguel, cierre B2).
function formatDate() {
  return new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

export default function HomePage({ userRole = 'admin', isOwner = false, onNavigate = () => {} }) {
  const esAdmin = userRole === 'admin' || isOwner;
  const esCelular = useMediaAngosto();
  const visibles = PANELES.filter((p) => esAdmin || !p.admin);
  // C-B4: dos columnas repartidas por ALTURA REAL (el siguiente panel cae a la más corta,
  // orden aprobado como prioridad) — sin columnas con la mitad vacía. En celular, una columna.
  const { alturas, medir } = useAlturasDePanel();
  const columnas = esCelular ? [visibles] : repartirEnColumnas(visibles, alturas);
  const alertaCosteo = useAlertaCosteo();
  return (
    <div className="page-home mobile-safe page-shell">
      <section className="page-hero surface-soft home-hero module-hero">
        <div className="module-hero__copy">
          <div className="module-title-row">
            <h1 className="page-hero__title">Centro de control</h1>
          </div>
          <div className="hero-chips">
            <span className="hero-chip">Hoy: {formatDate()}</span>
          </div>
        </div>
      </section>

      {alertaCosteo && <div className="insight-card ini-alerta"><strong>{alertaCosteo}</strong></div>}

      <div className={`pnl-cols${esCelular ? ' pnl-cols--una' : ''}`}>
        {columnas.map((col, ci) => (
          <div className="pnl-col" key={ci}>
            {col.map((def) => (
              <div key={def.id} ref={medir(def.id)}>
                <Panel
                  def={def} esAdmin={esAdmin} esCelular={esCelular}
                  primero={visibles[0] && def.id === visibles[0].id} onNavigate={onNavigate}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
